import React from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Zoom,
    Chip,
    Fade,
    IconButton,
    alpha,
    useTheme,
    useMediaQuery,
    Tooltip,
    Dialog
} from '@mui/material';
import {
    AccessTime,
    Edit,
    Delete,
    Add,
    ContentCopy,
    Close
} from '@mui/icons-material';

interface Booking {
    id: number;
    date: string;
    service: string;
    startTime: string;
    endTime: string;
    // Add optional fields to avoid type errors in grid if they are passed in "Booking"
    _id?: string;
    originalBookingId?: string;
    serviceId?: string;
    isReadOnly?: boolean;
    // Control edit/delete visibility explicitly
    allowEditDelete?: boolean;
    originalDetails?: {
        ispaymentVeified?: boolean;
        activeJobflow?: string;
        status?: string;
    };
}

interface CalendarGridProps {
    currentMonth: Date;
    bookings: Booking[];
    onDateClick: (date: string) => void;
    onDeleteBooking: (id: number | string) => void;
    onDuplicateBooking: (date: string) => void;
    onEditBooking: (booking: Booking) => void;
    onDuplicateBookingItem: (booking: Booking) => void;
    onBookingClick: (booking: Booking) => void;
    canDuplicate: boolean;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
    currentMonth,
    bookings,
    onDateClick,
    onDeleteBooking,
    onDuplicateBooking,
    onEditBooking,
    onDuplicateBookingItem,
    onBookingClick,
    canDuplicate
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // State for mobile details popup
    const [selectedDayDetails, setSelectedDayDetails] = React.useState<{ date: string; bookings: Booking[] } | null>(null);

    const getBookingBackgroundColor = (booking: Booking) => {
        if (booking.isReadOnly) {
            const status = booking.originalDetails?.status;

            if (status === 'Completed') return 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)';
            if (status === 'Accepted') return 'linear-gradient(135deg, #009688 0%, #00796B 100%)';
            if (status === 'Expired') return 'linear-gradient(135deg, #9E9E9E 0%, #616161 100%)';
            if (status === 'Declined') return 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)';
            if (status === 'Applied') return 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)';
            if (status === 'Generated') return 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)';

            return 'linear-gradient(135deg, #757575 0%, #616161 100%)';
        }

        if (booking.originalDetails?.status === 'Payment Pending') {
            return 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)';
        }

        return 'linear-gradient(135deg, #F475A4 0%, #FF6B9D 100%)';
    };

    const renderMobileDetailDialog = () => (
        <Dialog
            open={!!selectedDayDetails}
            onClose={() => setSelectedDayDetails(null)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    p: 1
                }
            }}
        >
            {selectedDayDetails && (
                <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} p={1}>
                        <Typography fontWeight={800} color="primary">
                            {(() => {
                                const [y, m, d] = selectedDayDetails.date.split('-').map(Number);
                                return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
                            })()}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => setSelectedDayDetails(null)}
                        >
                            <Close sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Box>

                    <Box sx={{ maxHeight: '60vh', overflowY: 'auto', px: 1 }}>
                        {selectedDayDetails.bookings.length === 0 ? (
                            <Typography textAlign="center" color="text.secondary" py={4}>
                                No bookings for this date.
                            </Typography>
                        ) : (
                            selectedDayDetails.bookings.map((booking) => (
                                <Paper
                                    key={booking.id}
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        mb: 1.5,
                                        background: getBookingBackgroundColor(booking),
                                        color: '#fff',
                                        borderRadius: 2,
                                        position: 'relative'
                                    }}
                                    onClick={() => onBookingClick(booking)}
                                >
                                    <Box>
                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                            <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '1rem' }}>
                                                {booking.service}
                                            </Typography>

                                            {/* Action Buttons */}
                                            {!booking.isReadOnly && booking.allowEditDelete !== false && (
                                                <Box display="flex" gap={1} sx={{ backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: 1, p: 0.5 }}>
                                                    {booking.originalDetails?.status !== 'Payment Pending' && (
                                                        <>
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: '#fff', p: 0.5 }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onDuplicateBookingItem(booking);
                                                                    setSelectedDayDetails(null);
                                                                }}
                                                            >
                                                                <ContentCopy sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: '#fff', p: 0.5 }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    onEditBooking(booking);
                                                                    setSelectedDayDetails(null);
                                                                }}
                                                            >
                                                                <Edit sx={{ fontSize: 18 }} />
                                                            </IconButton>
                                                        </>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        sx={{ color: '#fff', p: 0.5 }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onDeleteBooking(booking._id || booking.id);
                                                            setSelectedDayDetails(null);
                                                        }}
                                                    >
                                                        <Delete sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Box>
                                            )}
                                        </Box>

                                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                                            <AccessTime sx={{ fontSize: 16, opacity: 0.9 }} />
                                            <Typography variant="body2" sx={{ opacity: 0.95 }}>
                                                {booking.startTime} - {booking.endTime}
                                            </Typography>
                                        </Box>

                                        {booking.isReadOnly && booking.originalDetails?.status && (
                                            <Chip
                                                label={booking.originalDetails.status}
                                                size="small"
                                                sx={{
                                                    mt: 1,
                                                    height: 20,
                                                    fontSize: '0.65rem',
                                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                                    color: '#fff',
                                                    fontWeight: 700
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Paper>
                            ))
                        )}
                    </Box>
                </>
            )}
        </Dialog>
    );

    const getDaysInMonth = (date: Date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        return {
            daysInMonth: lastDay.getDate(),
            startingDayOfWeek: firstDay.getDay(),
            year,
            month
        };
    };

    const isDateDisabled = (day: number, month: number, year: number): boolean => {
        const date = new Date(year, month, day);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return date < today;
    };

    const getBookingsForDate = (day: number, month: number, year: number): Booking[] => {
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        return bookings.filter(b => b.date === dateStr);
    };

    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);

    const renderCalendarDays = () => {
        const days: JSX.Element[] = [];
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(<Grid item xs={12 / 7} key={`empty-${i}`}><Box /></Grid>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const disabled = isDateDisabled(day, month, year);
            const dateBookings = getBookingsForDate(day, month, year);
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            // Use manual local date construction to avoid timezone shifts
            const todayDate = new Date();
            const todayStr = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
            const isToday = dateStr === todayStr;

            const handleDateClick = () => {
                if (disabled) return;

                if (dateBookings.length > 0) {
                    setSelectedDayDetails({ date: dateStr, bookings: dateBookings });
                } else {
                    onDateClick(dateStr);
                }
            };

            days.push(
                <Grid item xs={12 / 7} key={day}>
                    <Zoom in={true} style={{ transitionDelay: `${day * 8}ms` }}>
                        <Paper
                            onClick={handleDateClick}
                            elevation={0}
                            sx={{
                                p: { xs: 0.5, sm: 1 },
                                minHeight: { xs: 'auto', sm: 110, md: 130 },
                                aspectRatio: { xs: '1/1', sm: 'unset' },
                                display: { xs: 'flex', sm: 'block' },
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                height: '100%',
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                backgroundColor: disabled
                                    ? alpha('#F5F5F5', 0.6)
                                    : isToday
                                        ? 'linear-gradient(135deg, #FFF0F6 0%, #FFE4EF 100%)'
                                        : dateBookings.length > 0
                                            ? (isMobile ? '#F475A4' : 'linear-gradient(135deg, #FFF5F9 0%, #FFEBF3 100%)')
                                            : '#FFFFFF',
                                border: '2px solid',
                                borderColor: isToday
                                    ? '#F475A4'
                                    : dateBookings.length > 0
                                        ? alpha('#F475A4', 0.4)
                                        : alpha('#E0E0E0', 0.6),
                                borderRadius: { xs: '50%', sm: 2.5 },
                                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': isToday ? {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '4px',
                                    background: 'linear-gradient(90deg, #F475A4 0%, #FF9AC4 100%)',
                                    boxShadow: '0 2px 8px rgba(244, 117, 164, 0.4)',
                                } : {},
                                '&:hover': {
                                    transform: !disabled ? 'translateY(-6px) scale(1.02)' : 'none',
                                    boxShadow: !disabled ? '0 12px 32px rgba(244, 117, 164, 0.25)' : 'none',
                                    borderColor: !disabled ? '#F475A4' : undefined,
                                }
                            }}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 0, sm: 1 }} flexDirection={isMobile ? 'column' : 'row'}>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={(isToday || (isMobile && dateBookings.length > 0)) ? 800 : 700}
                                    sx={{
                                        fontSize: { xs: '0.9rem', sm: '1.125rem' },
                                        color: disabled
                                            ? alpha('#000', 0.3)
                                            : (isMobile && dateBookings.length > 0 && !isToday)
                                                ? '#fff'
                                                : isToday
                                                    ? '#F475A4'
                                                    : '#333',
                                    }}
                                >
                                    {day}
                                </Typography>

                                {!isMobile && (
                                    <Box display="flex" gap={0.5}>
                                        {/* Plus Button for Duplication - REMOVED */}
                                        {dateBookings.length > 0 && (
                                            <Chip
                                                label={dateBookings.length}
                                                size="small"
                                                sx={{
                                                    height: { xs: 20, sm: 24 },
                                                    minWidth: { xs: 20, sm: 24 },
                                                    '& .MuiChip-label': {
                                                        px: { xs: 0.6, sm: 0.8 },
                                                        fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                                        fontWeight: 800
                                                    },
                                                    background: 'linear-gradient(135deg, #F475A4 0%, #FF6B9D 100%)',
                                                    color: '#fff',
                                                }}
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>

                            {/* Desktop/Tablet List (Mobile uses popup only) */}
                            {!isMobile && (
                                /* Desktop/Tablet List */
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxHeight: { xs: 55, sm: 70 },
                                        overflow: 'auto',
                                        px: 0.5,
                                        '&::-webkit-scrollbar': { width: '4px' },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: '#F475A4',
                                            borderRadius: '10px',
                                        }
                                    }}
                                >
                                    {dateBookings.map((booking: Booking, index) => {
                                        // Determine background color based on booking status
                                        const getBackgroundColor = () => {
                                            if (booking.isReadOnly) {
                                                const status = booking.originalDetails?.status;
                                                const jobFlow = booking.originalDetails?.activeJobflow;
                                                const isPaymentVerified = booking.originalDetails?.ispaymentVeified;

                                                // Completed -> Green (same as Accepted/Payment Success)
                                                // The user requested "Completed" to have a label. I'll keep the green background.
                                                // Accepted -> Green
                                                if (status === 'Completed') {
                                                    return 'linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)';
                                                }

                                                // Completed -> Teal
                                                if (status === 'Accepted') {
                                                    return 'linear-gradient(135deg, #009688 0%, #00796B 100%)';
                                                }

                                                // Expired -> Grey (Changed to match Legend)
                                                if (status === 'Expired') {
                                                    return 'linear-gradient(135deg, #9E9E9E 0%, #616161 100%)';
                                                }

                                                // Declined -> Dark Red
                                                if (status === 'Declined') {
                                                    return 'linear-gradient(135deg, #D32F2F 0%, #B71C1C 100%)';
                                                }

                                                // Applied -> Blue
                                                if (status === 'Applied') {
                                                    return 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)';
                                                }

                                                // Generated -> Orange
                                                if (status === 'Generated') {
                                                    return 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)';
                                                }

                                                // Fallback for other backend statuses -> Grey
                                                return 'linear-gradient(135deg, #757575 0%, #616161 100%)';
                                            }

                                            // Check if it is a "Payment Pending" draft (which is !isReadOnly but special)
                                            if (booking.originalDetails?.status === 'Payment Pending') {
                                                return 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)';
                                            }

                                            // User-created bookings (pink)
                                            return 'linear-gradient(135deg, #F475A4 0%, #FF6B9D 100%)';
                                        };

                                        return (
                                            <Fade in={true} style={{ transitionDelay: `${index * 50}ms` }} key={booking.id}>
                                                <Tooltip
                                                    title={
                                                        <React.Fragment>
                                                            <Typography fontWeight={700}>{booking.service}</Typography>
                                                            <Typography sx={{ fontSize: '0.75rem' }}>
                                                                {booking.startTime} - {booking.endTime}
                                                            </Typography>
                                                            {booking.isReadOnly && booking.originalDetails?.status && (
                                                                <Typography sx={{ fontSize: '0.7rem', mt: 0.5, fontStyle: 'italic' }}>
                                                                    Status: {booking.originalDetails.status}
                                                                </Typography>
                                                            )}
                                                        </React.Fragment>
                                                    }
                                                    placement="top"
                                                    arrow
                                                >
                                                    <Paper
                                                        elevation={0}
                                                        sx={{
                                                            p: { xs: 0.5, sm: 0.75 },
                                                            mb: 0.6,
                                                            background: getBackgroundColor(),
                                                            color: '#fff',
                                                            borderRadius: 1.5,
                                                            overflow: 'hidden', // Prevent content from sticking out
                                                            // Disable cursor for Expired items
                                                            cursor: (booking.originalDetails?.status === 'Expired') ? 'default' : (booking.isReadOnly ? 'pointer' : 'pointer'),
                                                            '&:hover': { transform: booking.originalDetails?.status === 'Expired' ? 'none' : (booking.isReadOnly ? 'none' : 'scale(1.03)') }
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            // Open the details popup for this day instead of navigating immediately
                                                            setSelectedDayDetails({ date: dateStr, bookings: dateBookings });
                                                        }}
                                                    >
                                                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={0.5}>
                                                            <Box flex={1} minWidth={0}>
                                                                <Typography
                                                                    variant="caption"
                                                                    fontWeight={700}
                                                                    display="block"
                                                                    sx={{
                                                                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                                                        whiteSpace: 'nowrap',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                    }}
                                                                >
                                                                    {booking.service}
                                                                </Typography>

                                                                <Typography
                                                                    variant="caption"
                                                                    display="block"
                                                                    sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, opacity: 0.95 }}
                                                                >
                                                                    {booking.startTime} - {booking.endTime}
                                                                </Typography>

                                                                {/* Add Status Label for Read-Only Bookings */}
                                                                {booking.isReadOnly && booking.originalDetails?.status && (
                                                                    <Typography
                                                                        variant="caption"
                                                                        display="block"
                                                                        sx={{
                                                                            fontSize: '0.6rem',
                                                                            fontWeight: 800,
                                                                            mt: 0.2,
                                                                            opacity: 0.9,
                                                                            textTransform: 'uppercase',
                                                                            letterSpacing: '0.5px',
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                        }}
                                                                    >
                                                                        {booking.originalDetails.status}
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            {/* Only show edit/delete buttons for user-created bookings that allow it - REMOVED INLINE BUTTONS */}
                                                            {/* {!booking.isReadOnly && booking.allowEditDelete !== false && (
                                                                <Box display="flex" gap={0.4} flexShrink={0}>
                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{ color: '#fff', p: 0.5, backgroundColor: alpha('#fff', 0.2) }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onEditBooking(booking);
                                                                        }}
                                                                    >
                                                                        <Edit sx={{ fontSize: 14 }} />
                                                                    </IconButton>

                                                                    <IconButton
                                                                        size="small"
                                                                        sx={{ color: '#fff', p: 0.5, backgroundColor: alpha('#fff', 0.2) }}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onDeleteBooking(booking._id || booking.id);
                                                                        }}
                                                                    >
                                                                        <Delete sx={{ fontSize: 14 }} />
                                                                    </IconButton>
                                                                </Box>
                                                            )} */}
                                                        </Box>
                                                    </Paper>
                                                </Tooltip>
                                            </Fade>
                                        );
                                    })}
                                </Box>
                            )}
                        </Paper>
                    </Zoom>
                </Grid>
            );
        }
        return days;
    };

    return (
        <Box
            sx={{
                overflowX: 'auto',
                width: '100%',
                pb: { xs: 2, sm: 0 }
            }}
        >
            <Box sx={{ minWidth: { xs: '100%', sm: '100%' } }}>
                {/* Day Headers */}
                <Grid container spacing={{ xs: 0.25, sm: 0.5, md: 0.75 }} mb={{ xs: 0.5, sm: 0.5 }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <Grid item xs={12 / 7} key={day}>
                            <Paper
                                elevation={0}
                                sx={{
                                    py: { xs: 1.2, sm: 1.8 },
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, rgba(244, 117, 164, 0.12) 0%, rgba(255, 154, 196, 0.08) 100%)',
                                    borderRadius: { xs: 1.5, sm: 2 },
                                    border: '1px solid',
                                    borderColor: alpha('#F475A4', 0.15)
                                }}
                            >
                                <Typography
                                    fontWeight={800}
                                    sx={{
                                        fontSize: { xs: '0.65rem', sm: '0.95rem' },
                                        background: 'linear-gradient(135deg, #F475A4 0%, #D45E8C 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text'
                                    }}
                                >
                                    {isMobile ? day.slice(0, 1) : day}
                                </Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={{ xs: 0.25, sm: 0.5, md: 0.75 }}>
                    {renderCalendarDays()}
                </Grid>
            </Box>

            {/* Render Mobile Dialog */}
            {renderMobileDetailDialog()}
        </Box>
    );
};

export default CalendarGrid;
